import { Button } from '@neo/primitives/button'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { ChevronRight, GripVertical, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { SNAKE_CASE_VARIABLE_NAME } from '../../utils/flow-validation'

// Shared card-shell pieces for the agent_node variables sections (SER-5959):
// collapsible row chrome (head + animated body), add/delete affordances, and
// the field/label primitives both row kinds compose.

export const chip = 'rounded-md bg-bg3 px-2 py-0.5 text-xs-500 whitespace-nowrap'
export const rowShell = 'rounded-md border border-st2 bg-background'
export const headRow =
  'flex items-center gap-2.5 px-3 py-1.5 cursor-pointer select-none transition-colors hover:bg-hv1'
export const addButton = 'gap-2 w-fit my-1'

export const AddIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="shrink-0"
    role="presentation"
  >
    <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
)

const Caret = ({ open }: { open: boolean }) => (
  <ChevronRight
    size={14}
    className={`shrink-0 text-secondary transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
  />
)

export const DragHandle = () => (
  <GripVertical size={14} className="shrink-0 text-secondary cursor-grab" />
)

export const DeleteButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip content="Remove variable">
    <Button
      variant="ghostDestructive"
      size="iconSm"
      type="button"
      aria-label="Remove variable"
      data-testid="button-remove-variable"
      className="active:scale-95"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Trash2 size={16} />
    </Button>
  </Tooltip>
)

export const RowHead = ({
  open,
  onToggle,
  children,
}: {
  open: boolean
  onToggle: () => void
  children: ReactNode
}) => (
  <div
    className={headRow}
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggle()
      }
    }}
  >
    {children}
    <Caret open={open} />
  </div>
)

// Body stays mounted; expansion animates via grid-template-rows so height-to-auto
// transitions natively. Conditional rendering here would kill the transition.
export const ExpandableBody = ({ open, children }: { open: boolean; children: ReactNode }) => (
  <div
    className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
      open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
    }`}
  >
    <div className="min-h-0 overflow-hidden">
      <div className="px-3.5 pb-4 pt-1 flex flex-col gap-3.5">{children}</div>
    </div>
  </div>
)

// Same enforcement style as ai_agent_v2's variable_name (inline error, no
// transform); SNAKE_CASE_VARIABLE_NAME is the shared publish-time rule.
export const VariableNameError = ({ name }: { name: string }) =>
  name && !SNAKE_CASE_VARIABLE_NAME.test(name) ? (
    <Typography variant="XS/400" className="text-status-danger mt-1">
      Variable name must use lowercase letters, numbers, and underscores.
    </Typography>
  ) : null

export const FieldBlock = ({
  label,
  htmlFor,
  children,
}: {
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}) => (
  <div>
    <Label htmlFor={htmlFor} className="text-s-500 mb-1.5 block">
      {label}
    </Label>
    {children}
  </div>
)
